import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import { useMemo } from "react";
import { MeshPhysicalMaterial, MeshStandardMaterial } from "three";
import { isDeMaasLeafStyleLayoutCode } from "entities/configurator/model/layoutMeta";
import { deMaasBeamInsetMeters } from "entities/configurator/model/layoutGeometry";
import {
  getGeometryBounds2D,
  getOpeningEdgesX,
} from "../components/openingGeometry";
import { OpdekGLTFResult } from "../opdek/OpdekType";
import { PaneelGLTFResult } from "../paneel/PaneelType";
import { ScharnierGLTFResult } from "../scharnier/ScharnierType";
import { SchuifGLTFResult } from "../schuif/SchuifType";
import { TaatsGLTFResult } from "../taats/TaatsType";
import {
  getConstrainedDeMaasLayoutMetrics,
  getDeMaasLayoutMetrics,
  getDeMaasVerticalWindow,
} from "./deMaasLayoutUtils";

export const DeMaasLayout = ({
  chosenMaterial,
  nodes,
  doorIndex,
}: {
  chosenMaterial: MeshPhysicalMaterial | MeshStandardMaterial;
  nodes:
    | TaatsGLTFResult["nodes"]
    | SchuifGLTFResult["nodes"]
    | ScharnierGLTFResult["nodes"]
    | PaneelGLTFResult["nodes"]
    | OpdekGLTFResult["nodes"];
  doorIndex: number;
}) => {
  const { eigenwijs, getConfigurationWidth, getConfigurationHeight, layout } =
    useConfigurationOptionsContext();

  const currentHeight = getConfigurationHeight(0);
  const currentWidth = getConfigurationWidth(doorIndex);
  const preferredMetrics = useMemo(
    () => getDeMaasLayoutMetrics(currentWidth, currentHeight),
    [currentHeight, currentWidth],
  );
  const curveBounds = useMemo(
    () => getGeometryBounds2D(nodes.beam_demaas.geometry),
    [nodes.beam_demaas.geometry],
  );
  const beamBounds = useMemo(
    () => getGeometryBounds2D(nodes.beam.geometry),
    [nodes.beam.geometry],
  );
  const glassBounds = useMemo(
    () => getGeometryBounds2D(nodes.glass.geometry),
    [nodes.glass.geometry],
  );
  const openingLeftX = useMemo(
    () => getOpeningEdgesX(glassBounds, currentWidth).left,
    [currentWidth, glassBounds],
  );
  const isLeafStyleDeMaasLayout = isDeMaasLeafStyleLayoutCode(layout?.code);
  const verticalWindow = useMemo(() => {
    return getDeMaasVerticalWindow({
      layoutCode: layout?.code,
      currentHeight,
      beamBounds,
      beams: eigenwijs?.beams,
      isLeafStyleDeMaasLayout,
      beamInsetMeters: deMaasBeamInsetMeters,
    });
  }, [
    beamBounds,
    currentHeight,
    eigenwijs?.beams,
    isLeafStyleDeMaasLayout,
    layout?.code,
  ]);

  const metrics = useMemo(
    () =>
      verticalWindow
        ? getConstrainedDeMaasLayoutMetrics({
            preferredMetrics,
            curveBounds,
            openingLeftX,
            verticalWindow,
          })
        : preferredMetrics,
    [curveBounds, openingLeftX, preferredMetrics, verticalWindow],
  );
  const { scale, centerX, centerY } = metrics;

  return (
    <mesh
      castShadow
      receiveShadow
      geometry={nodes.beam_demaas.geometry}
      material={chosenMaterial}
      scale-y={scale}
      scale-x={scale}
      position={[centerX, centerY, 0]}
    />
  );
};
